import { useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useHistory, useParams } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Fade, Grid, Hidden, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  Portal_FieldsFragment,
  usePortal_GetPortalsByConciergeUserIdQuery,
  User_CreateUpdateUserInput,
  User_GetAllConciergeUsersDocument,
  useUser_DeleteUserMutation,
  useUser_GetUserByIdQuery,
  useUser_UpdateUserMutation,
} from 'queries';
import { Trash } from 'ts-react-feather-icons';
import * as yup from 'yup';

import Spinner from 'common/spinner';
import HomeLayout from 'layouts/home-layout';
import ConciergeForm from 'pages/common/concierge/concierge-form';
import ConciergePath from 'pages/common/concierge/concierge-path';
import ManageAssignedPortals from 'pages/common/portal/manage-assigned-portals';
import ProfileAvatar from 'pages/my-profile/components/profile-avatar';
import { NotificationSeverity, showAlert } from 'utils/notification-manager';

const useStyles = makeStyles(() => ({
  container: {
    padding: '5%',
    flexGrow: 1,
  },
  title: {
    color: '#8DA8B2',
  },
  subtitle: {
    color: '#096094',
    width: '55vw',
    marginTop: '2vh',
    marginLeft: '2vh',
    marginBottom: '4vh',
  },
  grid: {
    backgroundColor: '#ffffff',
    marginTop: '20px',
    display: 'flex',
    boxShadow: '0px 2px 5px rgba(0, 0, 0, 0.25)',
    minHeight: '50vh',
    maxWidth: '100%',
  },

  saveButton: {
    backgroundColor: '#F5DE76',
  },
  cancelButton: {
    color: '#ffffff',
    backgroundColor: '#8DA8B2',
    marginLeft: '15px',
  },
  text: {
    color: 'black',
  },
  buttonContainer: {
    float: 'right',
    marginBottom: '10vh',
    width: '45vw',
  },
  parentRoute: {
    color: '#8DA8B2',
    cursor: 'pointer',
  },
  routeIconContainer: {
    verticalAlign: 'middle',
  },
  portalContainer: {
    marginBottom: '50px',
  },
  padding: {
    padding: '20px',
    float: 'right',
  },
  deleteButton: {
    backgroundColor: '#8DA8B2',
    float: 'right',
    right: '20px',
    color: '#ffffff',
    marginTop: '20px',
  },
}));

const schema = yup.object().shape({
  firstName: yup.string().required('Required'),
  lastName: yup.string().required('Required'),
  email: yup.string().email().required('Required'),
  phone: yup.string().required('Please enter a phone number'),
  calendar: yup.string(),
  message: yup.string().required('Please enter a message'),
});

type EditConciergeValues = User_CreateUpdateUserInput & {
  portals?: Portal_FieldsFragment[];
};

const EditConcierge = () => {
  const classes = useStyles();
  const history = useHistory();
  const { userId } = useParams<Record<string, string>>();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [avatar, setAvatar] = useState<any>();

  const { data: userData, loading: userLoading } = useUser_GetUserByIdQuery({
    variables: { id: userId },
    onError: (e) =>
      showAlert({
        message: 'Error getting user by ID: ' + e.message,
        severity: NotificationSeverity.ERROR,
      }),
  });

  const {
    data: portalsData,
    refetch: refetchPortalsData,
    loading: portalsDataLoading,
  } = usePortal_GetPortalsByConciergeUserIdQuery({
    variables: { id: userId },
    onError: (e) =>
      showAlert({
        message: 'Error getting portals by concierge ID: ' + e.message,
        severity: NotificationSeverity.ERROR,
      }),
  });

  const [updateUser, { loading: updateUserExecuting }] = useUser_UpdateUserMutation({
    onCompleted: () => {
      refetchPortalsData({ id: userId });
      history.push(`/home/concierges`);
    },
    onError: (e) =>
      showAlert({
        message: `Error updating user: ${e.message}`,
        severity: NotificationSeverity.ERROR,
      }),
  });

  const [deleteUser, { loading: deleteUserExecuting }] = useUser_DeleteUserMutation({
    variables: { id: userId },
    refetchQueries: [{ query: User_GetAllConciergeUsersDocument }],
    onCompleted: () => history.push(`/home/concierges`),
    onError: (e) =>
      showAlert({
        message: `Error deleting user: ${e.message}`,
        severity: NotificationSeverity.ERROR,
      }),
  });

  const formMethods = useForm<EditConciergeValues>({
    // @ts-expect-error - RHF V7 limitation #7895
    resolver: yupResolver(schema),
    values: {
      firstName: userData?.data.firstName || '',
      lastName: userData?.data.lastName || '',
      email: userData?.data.email || '',
      phone: userData?.data.phone || '',
      calendar: userData?.data.calendar || '',
      message: userData?.data.message || '',
      isConcierge: true,
      isSuperAdmin: false,
      portals: portalsData?.data || [],
    },
  });

  const submitForm = ({ portals, ...formData }: EditConciergeValues) => {
    updateUser({
      variables: {
        input: {
          ...formData,
          id: userId,
          avatar,
          portalIds: portals?.map((portal) => portal.id) || [],
          isConcierge: true,
          isSuperAdmin: false,
        },
      },
    });
  };

  if (!userData || userLoading) {
    return <Spinner />;
  }
  if (!portalsData || portalsDataLoading) {
    return <Spinner />;
  }

  return (
    <>
      <HomeLayout showPortalName={false}>
        <Fade in={true}>
          <Grid className={classes.container}>
            <ConciergePath path="Edit Concierge Account" />
            <Grid className={classes.grid}>
              <Grid container>
                <Grid xs={12} item md={4} lg={2}>
                  <ProfileAvatar
                    setAvatar={setAvatar}
                    avatarUrl={userData.data.avatarUrl || undefined}
                  />
                </Grid>
                <Grid xs={12} item md={8} lg={9}>
                  <Grid container>
                    <Grid spacing={3} xs={10} item md={8} lg={10}>
                      <Typography className={classes.subtitle} variant="h5">
                        Concierge Information
                      </Typography>
                    </Grid>
                    <Grid spacing={3} xs={2} item md={4} lg={2}>
                      <Button className={classes.deleteButton} onClick={() => deleteUser()}>
                        <Trash size="15" />
                        <Hidden mdDown={true}>Delete Concierge</Hidden>
                      </Button>
                    </Grid>
                  </Grid>
                  <FormProvider {...formMethods}>
                    <form onSubmit={formMethods.handleSubmit(submitForm)}>
                      <ConciergeForm />
                      <Controller
                        name="portals"
                        control={formMethods.control}
                        render={({ field: { value, onChange } }) => (
                          <ManageAssignedPortals portals={value || []} setPortals={onChange} />
                        )}
                      />

                      <Grid xs={9} className={classes.padding}>
                        <Button
                          type="submit"
                          className={classes.saveButton}
                          disabled={
                            !formMethods.formState.isDirty ||
                            formMethods.formState.isSubmitting ||
                            updateUserExecuting ||
                            deleteUserExecuting
                          }
                        >
                          Save Changes
                        </Button>
                        <Button
                          className={classes.cancelButton}
                          onClick={() => history.push(`/home/concierges`)}
                        >
                          Cancel
                        </Button>
                      </Grid>
                    </form>
                  </FormProvider>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Fade>
      </HomeLayout>
    </>
  );
};

export default EditConcierge;
